import { ClickAwayListener, Typography } from '@mui/material';
import IconBottomArrow from 'crono-fe-common/icons/Icon-Bottom-Arrow';
import IconTopArrow from 'crono-fe-common/icons/Icon-Top-Arrow';
import { colors } from 'crono-fe-common/theme';
import { CSSProperties, useState } from 'react';
import { SingleSelectWrapper } from './style';
import React from 'react';

export interface ISelectOption<T = string> {
  label: string;
  value: T;
}

interface IProps<T> {
  selectedOption?: ISelectOption<T> | null;
  handleSelectedOption: (option: ISelectOption<T>) => void;
  options: ISelectOption<T>[];
  placeholderText?: string;
  openUp?: boolean; //used for dropdowns that open upwards
  style?: React.CSSProperties;
  height?: string | number;
  disabled?: boolean;
  fitContent?: boolean; //used to make dropdown height fit content without scroll
  renderOption?: (
    option: ISelectOption<T>,
    context: {
      index: number;
      isSelected: boolean;
    },
  ) => React.ReactNode;
  renderFooter?: () => React.ReactNode;
  renderSelectedLabel?: (params: {
    placeholderText: string;
    selectedOption?: ISelectOption<T> | null;
  }) => React.ReactNode;
  onFooterClick?: () => void;
  dropdownStyles?: CSSProperties;
}

/** @deprecated Use Select from `@crono-one/design-system` instead. */
export const SingleSelect = <T,>({
  selectedOption,
  handleSelectedOption,
  options,
  placeholderText = 'Select an option',
  openUp = false,
  style = {},
  height,
  disabled = false,
  fitContent = false,
  renderOption = DefaultOptionRenderer,
  renderFooter,
  renderSelectedLabel = DefaultSelectedLabelRenderer,
  onFooterClick,
  dropdownStyles = {},
}: IProps<T>) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <ClickAwayListener
      onClickAway={() => setIsDropdownOpen(false)}
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
    >
      <SingleSelectWrapper style={style}>
        <div className="single-select" data-testid="single-select">
          <div
            className={`single-select__label${
              disabled ? ' single-select__label--disabled' : ''
            }`}
            style={{ height: height || '40px' }}
            onClick={() => {
              if (disabled) return;
              setIsDropdownOpen((prev) => !prev);
            }}
          >
            {renderSelectedLabel({ selectedOption, placeholderText })}

            {disabled ? null : isDropdownOpen ? (
              <IconTopArrow />
            ) : (
              <IconBottomArrow />
            )}
          </div>

          {isDropdownOpen && (
            <div
              className="single-select__dropdown"
              style={{
                bottom: openUp ? '100%' : 'auto',
                top: openUp ? 'auto' : '100%',
                marginTop: openUp ? '0' : '4px',
                marginBottom: openUp ? '4px' : '0',
                ...(fitContent && { maxHeight: 'fit-content' }),
                ...dropdownStyles,
              }}
            >
              {options.map((option, index) => (
                <div
                  key={index}
                  className="single-select__option"
                  onClick={() => {
                    handleSelectedOption(option);
                    setIsDropdownOpen(false);
                  }}
                >
                  {renderOption(option, {
                    index,
                    isSelected: selectedOption?.value === option?.value,
                  })}
                </div>
              ))}

              {renderFooter && (
                <div className="single-select__footer" onClick={onFooterClick}>
                  {renderFooter()}
                </div>
              )}
            </div>
          )}
        </div>
      </SingleSelectWrapper>
    </ClickAwayListener>
  );
};

export function DefaultOptionRenderer<T>(
  option: ISelectOption<T>,
  context: { isSelected: boolean },
): React.ReactNode {
  const { isSelected } = context;

  return (
    <Typography
      fontSize={12}
      fontWeight={500}
      color={isSelected ? colors.primary : colors.black}
      textOverflow="ellipsis"
      overflow="hidden"
      whiteSpace="nowrap"
    >
      {option.label}
    </Typography>
  );
}

export function DefaultSelectedLabelRenderer<T>({
  selectedOption,
  placeholderText,
}: {
  selectedOption?: ISelectOption<T> | null;
  placeholderText: string;
}): React.ReactNode {
  return (
    <Typography
      fontSize={14}
      fontWeight={500}
      color={selectedOption ? colors.black : colors.grey2}
      textOverflow="ellipsis"
      overflow="hidden"
      whiteSpace="nowrap"
    >
      {selectedOption ? selectedOption.label : placeholderText}
    </Typography>
  );
}
